import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { BadgeCheck, Loader2, GraduationCap, Mail, ArrowLeft } from "lucide-react";

const DOMAIN = "srmist.edu.in";

export default function AuthDialog({ open, onOpenChange }) {
  const { requestOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [name, setName] = useState("");
  const [regNo, setRegNo] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("email"); setEmail(""); setIsNew(false); setName(""); setRegNo(""); setCode(""); setRemember(true); setCooldown(0);
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [open]);

  const startCooldown = (secs) => {
    setCooldown(secs);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const res = await requestOtp(email.trim());
    setLoading(false);
    if (res.ok) {
      setIsNew(res.isNew);
      setStep("otp");
      setCode("");
      startCooldown(res.resendAfter || 30);
      toast.success(`We've emailed a 6-digit code to ${email.trim()}`);
    } else {
      toast.error(res.error);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || loading) return;
    setLoading(true);
    const res = await requestOtp(email.trim());
    setLoading(false);
    if (res.ok) { startCooldown(res.resendAfter || 30); toast.success("A new code is on its way."); }
    else toast.error(res.error);
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (isNew && !name.trim()) { toast.error("Please enter your full name."); return; }
    setLoading(true);
    const res = await verifyOtp({ email: email.trim(), code: code.trim(), name: name.trim(), reg_no: regNo.trim(), remember });
    setLoading(false);
    if (res.ok) { toast.success("You're in. Welcome to ReKTR!"); onOpenChange(false); }
    else { setCode(""); toast.error(res.error); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0" data-testid="auth-dialog">
        <div className="bg-ink text-white px-6 py-5">
          <div className="flex items-center gap-2 label-mono text-brand-100/80">
            <GraduationCap size={15} /> SRM KTR verified access
          </div>
          <DialogHeader className="mt-2 text-left space-y-1">
            <DialogTitle className="text-white font-display text-2xl">
              {step === "email" ? "Sign in to ReKTR" : "Enter your code"}
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              {step === "email" ? (
                "Use your SRM student email — we'll send a one-time code. No password needed."
              ) : (
                <>We emailed a 6-digit code to <span className="text-white font-medium">{email.trim()}</span>. It expires in 10 minutes.</>
              )}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6">
          {step === "email" ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div className="space-y-1.5">
                <Label>SRM Email</Label>
                <Input
                  required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={`you@${DOMAIN}`} data-testid="auth-email-input" autoFocus
                />
                <p className="text-xs text-slate-400">Only verified <span className="font-mono">@{DOMAIN}</span> students can join.</p>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-700 text-white h-11" data-testid="auth-send-code-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <><Mail size={17} className="mr-2" /> Send login code</>}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <div className="space-y-1.5">
                <Label>6-digit code</Label>
                <Input
                  required inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••••••" autoFocus
                  className="text-center text-2xl tracking-[0.6em] font-mono h-14"
                  data-testid="otp-code-input"
                />
              </div>
              {isNew && (
                <>
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Arjun Krishnan" data-testid="otp-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Registration No. <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Input value={regNo} onChange={(e) => setRegNo(e.target.value)} placeholder="RA2211003010001" data-testid="otp-regno-input" />
                  </div>
                </>
              )}
              <Button type="submit" disabled={loading || code.length < 6} className="w-full bg-brand hover:bg-brand-700 text-white h-11" data-testid="otp-verify-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & sign in"}
              </Button>
              <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none" htmlFor="remember-device">
                <Checkbox id="remember-device" checked={remember} onCheckedChange={(v) => setRemember(v === true)} data-testid="otp-remember-checkbox" />
                Keep me signed in on this device for 30 days
              </label>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => { setStep("email"); setCode(""); }} className="text-slate-500 hover:text-ink flex items-center gap-1" data-testid="otp-change-email-btn">
                  <ArrowLeft size={14} /> Change email
                </button>
                <button
                  type="button" onClick={resend} disabled={cooldown > 0 || loading}
                  className="text-brand hover:text-brand-700 disabled:text-slate-400 disabled:cursor-not-allowed" data-testid="otp-resend-btn"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500 justify-center">
            <BadgeCheck size={14} className="text-emerald-500" />
            Only verified SRM students. No outsiders, ever.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
